import type React from "react";
import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { API_URL, errorDetail, fetchJson } from "@/lib/admin-api";
import {
  ClassificationBadge,
  ClassificationToggleRow,
  EnableClassificationDialog,
  type ClassificationJobCounts,
  type ClassificationStatusEntry,
} from "@/components/admin/classification-schedule";
import { DevicePath } from "@/components/admin/device-path";
import { InviteUsersDialog } from "@/components/admin/invite-old-users";
import { Pagination, Table, Td, type Column } from "@/components/admin/table";
import { usePaged } from "@/components/admin/use-paged";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

/**
 * POS2-1383 — every live FoodSight site, with its devices and its classification schedule.
 *
 * DataBridge reaches a schedule only through a site the old platform had. This page reaches every
 * site by `site_id`, so a customer created in FoodSight, the commissioning site and the schedules
 * POS2-1401 creates can be seen and switched here. Both pages edit the same config row. There are
 * no import tools: those stay on DataBridge and retire with the old database. Staff-only and
 * English, like every `/admin/*` page.
 */

interface AdminSiteDevice {
  id: string;
  device_serial: string;
  name: string | null;
  status: string;
  kitchen_name: string;
  last_capture_at: string | null;
  last_source: "device" | "imported" | null;
  /** Its latest direct capture; null is a device that has not cut over (POS2-1018). */
  last_device_at: string | null;
}

interface AdminSite {
  id: string;
  name: string;
  status: "active" | "inactive";
  org_id: string;
  org_name: string;
  account_id: string;
  account_name: string;
  timezone: string;
  kitchens: string[];
  /** Everyone whose access reaches the site. */
  users: number;
  classification: ClassificationStatusEntry;
  devices: AdminSiteDevice[];
}

/** Live waste events in disjoint buckets that sum to `events`. */
/**
 * Live waste events in disjoint buckets that sum to `events`. Each classifier's events are split
 * into those still counted as waste and those discarded (`*_discarded`): by the classifier
 * itself, or by a labeller afterwards, which leaves the source as it was.
 */
interface SiteEventCounts {
  events: number;
  ai: number;
  ai_discarded: number;
  food_map: number;
  food_map_discarded: number;
  manual: number;
  manual_discarded: number;
  /** Classified before June 2026, when the source of a classification began to be recorded. */
  legacy: number;
  legacy_discarded: number;
  /** Not classified yet. */
  pending: number;
}

interface SiteClassification {
  site_id: string;
  enabled: boolean;
  device_serials: string[] | null;
  start_date: string | null;
  end_date: string | null;
  last_run_id: string | null;
  counts: ClassificationJobCounts;
  event_counts: SiteEventCounts;
}

const ALL = "all";
// DataBridge's cadence: refresh the schedule while its run is draining.
const POLL_INTERVAL_MS = 10_000;

const COLUMNS: Column[] = [
  { label: "Site" },
  { label: "Status" },
  { label: "Devices", numeric: true },
  { label: "Last capture" },
  { label: "Classification" },
];

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function lastCapture(site: AdminSite): string | null {
  const times = site.devices.flatMap((d) => (d.last_capture_at ? [d.last_capture_at] : []));
  if (times.length === 0) return null;
  return times.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b));
}

export default function AdminSitesPage() {
  const headers = useApiHeaders();
  const [search, setSearch] = useState("");
  const [orgId, setOrgId] = useState(ALL);
  const [classificationOff, setClassificationOff] = useState(false);
  const [notCutOver, setNotCutOver] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery<AdminSite[], Error>({
    queryKey: ["admin", "sites"],
    queryFn: () => fetchJson<AdminSite[]>(`${API_URL}/api/v1/admin/sites`, headers),
    retry: false,
  });

  const sites = list.data ?? [];
  const orgs = [...new Map(sites.map((s) => [s.org_id, s.org_name])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );
  const needle = search.trim().toLowerCase();
  const shown = sites.filter(
    (s) =>
      (!needle || s.name.toLowerCase().includes(needle)) &&
      (orgId === ALL || s.org_id === orgId) &&
      (!classificationOff || !s.classification.enabled) &&
      (!notCutOver || s.devices.some((d) => d.last_device_at === null)),
  );
  const paged = usePaged(shown);
  const off = sites.filter((s) => !s.classification.enabled).length;
  const selected = sites.find((s) => s.id === selectedId) ?? null;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-display text-xl font-extrabold tracking-tight text-gray-900">Sites</h1>
        <p className="mt-1 text-sm text-gray-500" data-testid="sites-summary">
          {shown.length === sites.length ? (
            <>
              All sites · all organisations · <b className="text-gray-700">{sites.length}</b> sites
            </>
          ) : (
            <>
              Filtered · showing <b className="text-gray-700">{shown.length}</b> of {sites.length}{" "}
              sites
            </>
          )}{" "}
          · <b className="text-gray-700">{off}</b> with classification off
        </p>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-300" />
          <Input
            aria-label="Search sites"
            placeholder="Site name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="w-56" aria-label="Organisation">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All organisations</SelectItem>
            {orgs.map(([id, name]) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <Toggle
          label="Classification off"
          checked={classificationOff}
          onChange={setClassificationOff}
        />
        <Toggle
          label="Devices not reporting directly"
          checked={notCutOver}
          onChange={setNotCutOver}
        />
      </div>

      {list.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {errorDetail(list.error)}
        </p>
      ) : (
        <Table<AdminSite>
          columns={COLUMNS}
          rows={paged.page}
          isLoading={list.isLoading}
          footer={paged.bar ? <Pagination {...paged.bar} /> : undefined}
          empty={sites.length ? "No sites match these filters." : "No sites."}
          renderRow={(site, columns) => (
            <tr
              key={site.id}
              role="button"
              tabIndex={0}
              data-testid={`site-row-${site.id}`}
              className="cursor-pointer border-b border-black/[0.06] last:border-b-0 hover:bg-gray-50"
              onClick={() => setSelectedId(site.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setSelectedId(site.id);
              }}
            >
              <Td column={columns[0]}>
                <span className="block font-medium text-gray-900">{site.name}</span>
                <span className="block text-xs text-gray-400">
                  {site.org_name} · {site.account_name}
                </span>
              </Td>
              <Td column={columns[1]}>
                {site.status === "active" ? (
                  "Active"
                ) : (
                  <span className="text-gray-400">Inactive</span>
                )}
              </Td>
              <Td column={columns[2]}>
                <span
                  className={`tabular-nums ${site.devices.length ? "text-gray-900" : "text-gray-300"}`}
                >
                  {site.devices.length}
                </span>
              </Td>
              <Td column={columns[3]}>{formatTime(lastCapture(site))}</Td>
              <Td column={columns[4]}>
                <ScheduleStatus classification={site.classification} />
              </Td>
            </tr>
          )}
        />
      )}

      <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {selected && <SiteSheet key={selected.id} site={selected} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </label>
  );
}

/** Off, or DataBridge's badge with the last run's job counts. */
function ScheduleStatus({ classification }: { classification: ClassificationStatusEntry }) {
  if (!classification.enabled) return <span className="text-gray-400">Off</span>;
  const { pending, running, done, failed } = classification.counts;
  return (
    <>
      <ClassificationBadge classification={classification} />
      {pending + running + done + failed > 0 && (
        <span className="mt-1 block text-xs tabular-nums text-gray-400">
          Latest run: {pending} pending · {running} running · {done} done · {failed} failed
        </span>
      )}
    </>
  );
}

function SiteSheet({ site }: { site: AdminSite }) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [enableOpen, setEnableOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const url = `${API_URL}/api/v1/admin/sites/${site.id}/classification`;

  const schedule = useQuery<SiteClassification, Error>({
    queryKey: ["admin", "sites", site.id, "classification"],
    queryFn: () => fetchJson<SiteClassification>(url, headers),
    retry: false,
    refetchInterval: (query) => {
      const counts = query.state.data?.counts;
      return counts && counts.pending + counts.running > 0 ? POLL_INTERVAL_MS : false;
    },
  });

  // The prefix covers the list too, whose schedule column reads the same row.
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["admin", "sites"] });

  return (
    <>
      <SheetHeader>
        <SheetTitle>{site.name}</SheetTitle>
        <SheetDescription>
          {site.org_name} · {site.account_name}
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-6 px-4 pb-6 text-sm">
        <section className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Classification
          </h3>
          {schedule.isError ? (
            <p role="alert" className="text-red-700">
              {errorDetail(schedule.error)}
            </p>
          ) : !schedule.data ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <Schedule
              schedule={schedule.data}
              url={url}
              onConfigure={() => setEnableOpen(true)}
              onChanged={refresh}
            />
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Devices</h3>
          {site.devices.length === 0 ? (
            <p className="text-gray-500">No devices.</p>
          ) : (
            <ul className="divide-y divide-black/[0.06]">
              {site.devices.map((d) => (
                <li key={d.id} className="flex items-start justify-between gap-4 py-2">
                  <div>
                    <Link
                      to={`/admin/devices?device=${d.id}`}
                      className="font-mono font-medium text-gray-900 hover:underline"
                    >
                      {d.device_serial}
                    </Link>
                    <span className="block text-xs text-gray-400">
                      {[d.name, d.kitchen_name, d.status].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <div className="text-right text-xs">
                    <DevicePath device={d} />
                    <span className="block text-gray-400">{formatTime(d.last_capture_at)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Users</h3>
          <p className="text-gray-900">
            <span className="tabular-nums">{site.users}</span> with access
          </p>
          <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
            Invite old-platform users
          </Button>
        </section>

        <section className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400">Details</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
            <Detail term="Organisation">{site.org_name}</Detail>
            <Detail term="Account">{site.account_name}</Detail>
            <Detail term="Timezone">{site.timezone}</Detail>
            <Detail term="Kitchens">{site.kitchens.join(", ") || "—"}</Detail>
          </dl>
        </section>
      </div>

      <EnableClassificationDialog
        open={enableOpen}
        onOpenChange={setEnableOpen}
        classificationUrl={url}
        siteName={site.name}
        devices={site.devices.map((d) => ({ serial: d.device_serial, name: d.name }))}
        // Imported sites are regular sites here, and this page has no watch state (POS2-1383).
        watchCopyingOldLabels={false}
        onChanged={refresh}
      />
      <InviteUsersDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        siteId={site.id}
        siteName={site.name}
      />
    </>
  );
}

function Schedule({
  schedule,
  url,
  onConfigure,
  onChanged,
}: {
  schedule: SiteClassification;
  url: string;
  onConfigure: () => void;
  onChanged: () => void;
}) {
  const entry: ClassificationStatusEntry = {
    enabled: schedule.enabled,
    last_run_id: schedule.last_run_id,
    open_ended: schedule.end_date === null,
    counts: schedule.counts,
    source_counts: null,
  };
  const events = schedule.event_counts;
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        <Detail term="Schedule">
          <ScheduleStatus classification={entry} />
        </Detail>
        <Detail term="Devices">
          {schedule.device_serials === null ? "All devices" : schedule.device_serials.join(", ")}
        </Detail>
        <Detail term="From">
          {schedule.start_date ? formatTime(schedule.start_date) : "No start date"}
        </Detail>
        <Detail term="Until">
          {schedule.end_date ? formatTime(schedule.end_date) : "Open-ended"}
        </Detail>
      </dl>
      <Coverage counts={events} />
      <ClassificationToggleRow
        classificationUrl={url}
        classification={entry}
        enableBlockedReason={null}
        onConfigure={onConfigure}
        onChanged={onChanged}
      />
    </>
  );
}

function Detail({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-gray-400">{term}</dt>
      <dd className="text-gray-900">{children}</dd>
    </>
  );
}

/**
 * How much of the site's history is classified, and by whom. Each classifier counts everything it
 * classified, its discards included, so the AI's row matches what its runs finished.
 */
function Coverage({ counts }: { counts: SiteEventCounts }) {
  const classified = counts.events - counts.pending;
  // Every discard has one of these four sources: classification_source is ai, food_map, manual
  // or NULL.
  const discarded =
    counts.ai_discarded +
    counts.food_map_discarded +
    counts.manual_discarded +
    counts.legacy_discarded;
  const rows = [
    { label: "AI", kept: counts.ai, discarded: counts.ai_discarded },
    { label: "Food map", kept: counts.food_map, discarded: counts.food_map_discarded },
    { label: "Manual", kept: counts.manual, discarded: counts.manual_discarded },
    { label: "Source not recorded", kept: counts.legacy, discarded: counts.legacy_discarded },
  ].filter((row) => row.kept + row.discarded > 0);
  return (
    <div className="space-y-2">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5" data-testid="event-totals">
        <Detail term="Total events">
          <span className="tabular-nums">{counts.events.toLocaleString()}</span>
        </Detail>
        <Detail term="Total discarded">
          <span className="tabular-nums">{discarded.toLocaleString()}</span>
        </Detail>
        <Detail term="Classified">
          <span className="tabular-nums">{classified.toLocaleString()}</span>
          {counts.events > 0 && (
            <span className="text-gray-400">
              {" "}
              · {Math.floor((100 * classified) / counts.events)}%
            </span>
          )}
        </Detail>
      </dl>
      {rows.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left uppercase tracking-wide text-gray-400">
              <th className="py-1 font-medium">Classified by</th>
              <th className="py-1 text-right font-medium">Events</th>
              <th className="py-1 text-right font-medium">Discarded</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-t border-black/[0.06]">
                <td className="py-1 text-gray-900">{row.label}</td>
                <td className="py-1 text-right tabular-nums text-gray-900">
                  {(row.kept + row.discarded).toLocaleString()}
                </td>
                <td className="py-1 text-right tabular-nums text-gray-400">
                  {row.discarded.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {counts.pending > 0 && (
        <p className="text-xs text-gray-400">
          <span className="font-medium tabular-nums text-amber-600">
            {counts.pending.toLocaleString()}
          </span>{" "}
          unclassified: logged before the schedule starts, outside its devices, waiting for a photo,
          or failed.
        </p>
      )}
    </div>
  );
}
